import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSurface } from '@/contexts/SurfaceContext';
import type { GameWalletItem } from '@/features/finance/services/paymentService';
import PlayLoginDialog from '@/features/games/components/PlayLoginDialog';
import {
  canPlayGame,
  currentDeviceClass,
  openGameExternally,
  safeGameUrl,
} from '@/features/games/utils/playGame';
import { navigationFromState } from '@/utils/navigateBack';

type PlayableWallet = GameWalletItem & { depositsOpen?: boolean };
type CatalogGame = Parameters<typeof canPlayGame>[1];

export interface PlayGame {
  /**
   * Show Play for this game: the surface allows Play (`can('play')`; a site opened outside the app
   * also needs `can('externalLinks')`) and canPlayGame() (account, http(s) link, not under
   * maintenance or Coming soon). Always false on kiosks and an unknown surface.
   */
  canPlay: (wallet: PlayableWallet | null | undefined, catalogGame?: CatalogGame) => boolean;
  /** `game.playInFrame`: the in-app player (/play/:gameId); otherwise the "Your login" dialog. */
  play: (wallet: PlayableWallet) => void;
  /** Render once wherever `play` is used: the "Your login" dialog. */
  playDialog: ReactNode;
}

/** Play (spec docs/specs/2026-10-08-kiosk-web-split-design.md §4). */
export function usePlayGame(): PlayGame {
  const { can, isInstalledApp } = useSurface();
  const navigate = useNavigate();
  const location = useLocation();
  const [target, setTarget] = useState<PlayableWallet | null>(null);
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);

  const canPlay = useCallback<PlayGame['canPlay']>(
    (wallet, catalogGame) => {
      if (!wallet || !can('play') || !canPlayGame(wallet, catalogGame)) return false;
      return wallet.game.playInFrame === true || can('externalLinks');
    },
    [can]
  );

  const play = useCallback<PlayGame['play']>(
    (wallet) => {
      if (!canPlay(wallet)) return;
      if (wallet.game.playInFrame === true) {
        navigate(
          `/play/${encodeURIComponent(wallet.game.id)}`,
          navigationFromState(`${location.pathname}${location.search}`)
        );
        return;
      }
      setBlockedUrl(null);
      setTarget(wallet);
    },
    [canPlay, navigate, location.pathname, location.search]
  );

  const close = useCallback(() => {
    setTarget(null);
    setBlockedUrl(null);
  }, []);

  const openGame = useCallback(() => {
    const url = safeGameUrl(target?.game.gameUrl);
    if (!url) return;
    const win = openGameExternally(url, { device: currentDeviceClass(), installedApp: isInstalledApp });
    setBlockedUrl(win ? null : url);
  }, [target, isInstalledApp]);

  const playDialog = useMemo(
    () => (
      <PlayLoginDialog
        open={target != null}
        onClose={close}
        gameName={target?.game.name}
        username={target?.account?.gameUsername}
        password={target?.account?.password}
        onOpenGame={openGame}
        blockedUrl={blockedUrl}
      />
    ),
    [target, close, openGame, blockedUrl]
  );

  return { canPlay, play, playDialog };
}
